import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

// service
import { ApiService } from './api.service';

// models
import { Authenticator } from '../../models';

// constants
import { API_WEBAUTHN_AUTHENTICATOR, API_WEBAUTHN_REGISTER, API_WEBAUTHN_GET_CREDENTIALS } from '../../constants';

// utils
import * as cbor from 'cbor-web';

interface WebAuthnChallengeOptions {
  publicKey: Record<string, unknown>;
}

type PublicKeyCredentialWithJson = typeof PublicKeyCredential & {
  parseCreationOptionsFromJSON(json: unknown): PublicKeyCredentialCreationOptions;
  parseRequestOptionsFromJSON(json: unknown): PublicKeyCredentialRequestOptions;
};

const PublicKeyCredentialJson = PublicKeyCredential as PublicKeyCredentialWithJson;

@Injectable({
  providedIn: 'root'
})
export class WebAuthnService {

  constructor(
    private apiService: ApiService,
    private httpClient: HttpClient,
  ) { }

  getAll(): Observable<Authenticator[]> {
    return this.apiService.get(API_WEBAUTHN_AUTHENTICATOR) as Observable<Authenticator[]>;
  }

  getOne(id: number): Observable<Authenticator> {
    return this.apiService.get(`${API_WEBAUTHN_AUTHENTICATOR}${id}/`) as Observable<Authenticator>;
  }

  async create(): Promise<Authenticator> {
    const challenge = await this.httpClient.post(API_WEBAUTHN_REGISTER, null, { responseType: 'arraybuffer' }).toPromise();
    const options = cbor.decode(challenge) as WebAuthnChallengeOptions;
    const publicKey = PublicKeyCredentialJson.parseCreationOptionsFromJSON(options.publicKey);

    const attestation: PublicKeyCredential = await navigator.credentials.create({ publicKey }) as PublicKeyCredential;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/cbor'
      })
    };

    const payload = cbor.encode({
      "authenticatorAttachment": (<any>attestation).authenticatorAttachment,
      "attestationObject": (<any>attestation.response).attestationObject,
      "clientDataJSON": attestation.response.clientDataJSON,
    });

    return await this.httpClient.post(API_WEBAUTHN_AUTHENTICATOR, payload.buffer, httpOptions).toPromise() as Authenticator;
  }

  update(id: number, body: Partial<Authenticator>): Observable<Authenticator> {
    return <Observable<Authenticator>>this.apiService.put(`${API_WEBAUTHN_AUTHENTICATOR}${id}/`, body).pipe(
      map(res => res as Authenticator));
  }

  delete(id: number): Observable<void> {
    return this.httpClient.delete(`${API_WEBAUTHN_AUTHENTICATOR}${id}/`).pipe(map(res => null));
  }

  async getCredentials(): Promise<PublicKeyCredential> {
    const challenge = await this.httpClient.post(API_WEBAUTHN_GET_CREDENTIALS, null, { responseType: 'arraybuffer' }).toPromise();
    const options = cbor.decode(challenge) as WebAuthnChallengeOptions;
    const publicKey = PublicKeyCredentialJson.parseRequestOptionsFromJSON(options.publicKey);

    return await navigator.credentials.get({ publicKey }) as PublicKeyCredential;
  }
}
